import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  fetchBusinessHours,
  saveBusinessHours,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import {
  isValidTimeRange,
  SavedSnackbar,
  TabDescription,
  TimeRange,
  TimeRangeListEditor,
  UnsavedChangesBar,
  useDirtyNotifier,
  useSaveFeedback,
} from './SettingsShared';
import { BusinessHourSlot, ReservationConflict } from '../../../types/reservation';
import ConfirmDialog from '../../../Components/Dialog/ConfirmDialog';

interface Props {
  storeId: string;
  /** dirty（サーバ値と差分あり）の変化を親へ通知する（タブ切替時の確認用） */
  onDirtyChange?: (dirty: boolean) => void;
}

/** ISO 曜日（1=月 ... 7=日）の表示ラベル */
const DAY_LABELS = ['月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日'];

/** 曜日ごとのフォーム状態。「営業」でも保存時に時間帯が無ければエラーにする */
interface DayRow {
  isOpen: boolean;
  ranges: TimeRange[];
}

/** BusinessHourSlot[]（dayOfWeek: 1〜7）→ 曜日別フォーム状態へ変換（dirty 判定の基準にも使う） */
const toDayRows = (businessHours: BusinessHourSlot[]): DayRow[] => {
  const ranges: TimeRange[][] = Array.from({ length: 7 }, () => []);
  businessHours.forEach((slot) => {
    if (slot.dayOfWeek >= 1 && slot.dayOfWeek <= 7) {
      ranges[slot.dayOfWeek - 1].push({ open: slot.open, close: slot.close });
    }
  });
  ranges.forEach((r) => r.sort((a, b) => a.open.localeCompare(b.open)));
  // 時間帯が 1 件もない曜日は定休日として扱う
  return ranges.map((r) => ({ isOpen: r.length > 0, ranges: r }));
};

/**
 * タブ③営業時間。曜日ごとに「定休日/営業」をラジオで選び、営業日のみ時間帯を編集する。
 * 保存で全置換 PUT（定休日の曜日は時間帯を送らない）。
 * close <= open の時間帯は「翌日 close」（日跨ぎ営業。例: 17:00〜翌03:00）として BE でも有効値。
 * 未保存の変更がある間は画面下部に sticky 保存バーを表示する（フィードバック3）。
 */
const BusinessHoursTab: React.FC<Props> = ({ storeId, onDirtyChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const businessHours = useSelector((state: RootState) => state.reservationSettings.businessHours);

  // 曜日ごとの状態（index 0=月 ... 6=日）。サーバ値の取得完了時に同期する。
  const [rows, setRows] = useState<DayRow[]>(() =>
    Array.from({ length: 7 }, () => ({ isOpen: false, ranges: [] })),
  );
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  // B5: 影響予約ありで BE が 409 を返したときの確認ダイアログ状態。
  // 保存対象の slots と affectedCount を保持し、OK で confirm=true を付けて再送する。
  const [pendingConfirm, setPendingConfirm] = useState<{
    slots: { dayOfWeek: number; open: string; close: string }[];
    affectedCount?: number;
  } | null>(null);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  useEffect(() => {
    dispatch(fetchBusinessHours(storeId))
      .unwrap()
      .catch(showError)
      .finally(() => setLoaded(true));
  }, [dispatch, storeId, showError]);

  const baseline = useMemo(() => toDayRows(businessHours), [businessHours]);

  useEffect(() => {
    setRows(baseline);
  }, [baseline]);

  const dirty = loaded && JSON.stringify(rows) !== JSON.stringify(baseline);
  useDirtyNotifier(dirty, onDirtyChange);

  const updateDayRanges = (dayIndex: number, ranges: TimeRange[]) => {
    setRows((prev) => prev.map((row, i) => (i === dayIndex ? { ...row, ranges } : row)));
  };

  const updateDayOpen = (dayIndex: number, isOpen: boolean) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== dayIndex) return row;
        // 「営業」へ切り替えたとき時間帯が空ならデフォルト 00:00〜23:00 を1件入れる
        if (isOpen && row.ranges.length === 0) {
          return { isOpen, ranges: [{ open: '00:00', close: '23:00' }] };
        }
        return { ...row, isOpen };
      }),
    );
  };

  /**
   * 営業時間を保存する。
   * confirm 未指定で保存し、影響予約ありで BE が 409 を返したら確認ダイアログを出す（B5）。
   * ダイアログ OK 後は confirm=true で同じ slots を再送する。
   */
  const performSave = (
    slots: { dayOfWeek: number; open: string; close: string }[],
    confirm?: boolean,
  ) => {
    setSaving(true);
    dispatch(saveBusinessHours({ storeId, slots, confirm }))
      .unwrap()
      .then(() => {
        setPendingConfirm(null);
        showSaved();
      })
      .catch((rejected: ReservationConflict) => {
        // 影響予約ありで confirm 未指定 → 409。確認ダイアログを出す（再送用に slots を保持）。
        if (!confirm && rejected?.status === 409) {
          setPendingConfirm({ slots, affectedCount: rejected.affectedCount });
          return;
        }
        showError(rejected);
      })
      .finally(() => setSaving(false));
  };

  const save = () => {
    // 営業日に未入力の時間帯・時間帯ゼロがあれば保存前に弾く（close <= open は翌日扱いで有効）
    for (let i = 0; i < 7; i += 1) {
      if (!rows[i].isOpen) continue;
      if (rows[i].ranges.length === 0) {
        setError(`${DAY_LABELS[i]}は営業になっていますが時間帯がありません。時間帯を追加するか定休日にしてください。`);
        return;
      }
      if (rows[i].ranges.some((r) => !isValidTimeRange(r))) {
        setError(`${DAY_LABELS[i]}の時間帯に未入力のものがあります。`);
        return;
      }
    }
    const slots = rows.flatMap((row, i) =>
      row.isOpen ? row.ranges.map((r) => ({ dayOfWeek: i + 1, open: r.open, close: r.close })) : [],
    );
    performSave(slots);
  };

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TabDescription text="予約を受け付ける曜日と時間帯です。ここで設定した時間内にだけ予約枠が作られます" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          深夜営業の場合は終了時刻を開始時刻より前にすると翌日扱いになります（例: 17:00〜翌3:00）
        </Typography>
        {rows.map((row, dayIndex) => (
          <Box key={dayIndex}>
            {dayIndex > 0 && <Divider sx={{ my: 1 }} />}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', py: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ minWidth: 64, pt: 1, flexShrink: 0 }}>
                {DAY_LABELS[dayIndex]}
              </Typography>
              <RadioGroup
                row
                value={row.isOpen ? 'open' : 'closed'}
                onChange={(e) => updateDayOpen(dayIndex, e.target.value === 'open')}
                sx={{ flexShrink: 0 }}
              >
                <FormControlLabel
                  value="closed"
                  control={<Radio size="small" />}
                  label="定休日"
                  disabled={saving}
                />
                <FormControlLabel
                  value="open"
                  control={<Radio size="small" />}
                  label="営業"
                  disabled={saving}
                />
              </RadioGroup>
              {row.isOpen && (
                <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                  <TimeRangeListEditor
                    ranges={row.ranges}
                    onChange={(next) => updateDayRanges(dayIndex, next)}
                    disabled={saving}
                  />
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Paper>

      {/* 未保存の変更がある間だけ表示される sticky 保存バー（保存し忘れ防止） */}
      <UnsavedChangesBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={() => {
          setRows(baseline);
          setError(null);
        }}
      />

      {/* B5: 変更後の営業時間に合わない予約がある場合の確認ダイアログ */}
      <ConfirmDialog
        open={pendingConfirm !== null}
        onClose={() => setPendingConfirm(null)}
        onConfirm={() => {
          if (pendingConfirm) performSave(pendingConfirm.slots, true);
        }}
        title="営業時間の変更"
        message={
          typeof pendingConfirm?.affectedCount === 'number'
            ? `変更後の営業時間に合わない予約が${pendingConfirm.affectedCount}件あります。これらは別途ご対応が必要です。変更を続けますか？`
            : '変更後の営業時間に合わない予約があります。これらは別途ご対応が必要です。変更を続けますか？'
        }
        confirmText="変更を続ける"
        cancelText="キャンセル"
        confirmColor="warning"
        closeColor="inherit"
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default BusinessHoursTab;
